/**
 * Hero Elegant Section
 * Enhanced Version with Interactive Effects
 */

class HeroElegant {
  constructor(section) {
    this.section = section;
    this.id = section.id || `hero-elegant-${Math.random().toString(36).substr(2, 9)}`;
    this.animationEnabled = section.dataset.animationEnabled === 'true';
    this.animationType = section.dataset.animationType || 'fade-up';
    this.scrollIconType = section.dataset.scrollIconType || 'arrow-down';
    this.customButtonEnabled = section.dataset.customButton === 'true';
    this.parallaxEnabled = section.dataset.parallaxEnabled === 'true';
    this.scaleEffectEnabled = section.dataset.scaleEffect === 'true';
    
    this.content = section.querySelector('[data-animate]');
    this.scrollIndicator = section.querySelector('[data-scroll-indicator]');
    this.buttons = section.querySelectorAll('[data-hero-button]');
    this.bg = section.querySelector('[data-bg]');
    this.overlay = section.querySelector('[data-overlay]');
    this.bgElements = {
      desktop: section.querySelector('[data-bg-image], [data-bg-video]'),
      mobile: section.querySelector('[data-bg-image-mobile], [data-bg-video-mobile]')
    };
    
    this.observer = null;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.touchStartY = 0;
    this.touchMoveY = 0;
    this.lastScrollTop = 0;
    this.isMobile = window.innerWidth < 768;
    this.isTouching = false;
    
    this.init();
  }

  init() {
    // Ensure section has ID
    if (!this.section.id) {
      this.section.id = this.id;
    }
    
    // Always ensure content is visible (fallback for no JS)
    this.ensureContentVisibility();
    
    // Apply custom button styles if enabled
    if (this.customButtonEnabled) {
      this.applyCustomButtonStyles();
      this.setupButtonInteractions();
    }
    
    // Setup animations if enabled
    if (this.animationEnabled && this.content) {
      this.setupAnimations();
    }
    
    // Setup scroll indicator
    if (this.scrollIndicator) {
      this.setupScrollIndicator();
    }
    
    // Setup interactive effects
    this.setupInteractiveEffects();
    
    // Setup responsive adjustments
    this.setupResponsive();
    
    // Setup touch interactions
    this.setupTouchInteractions();
    
    // Add debug info if needed
    if (window.heroElegantDebug) {
      this.addDebugInfo();
    }
  }

  ensureContentVisibility() {
    // Remove any inline styles that might hide content
    const contentElements = this.section.querySelectorAll(
      '.hero-elegant__eyebrow, .hero-elegant__headline, .hero-elegant__text, .hero-elegant__buttons-wrapper'
    );
    
    contentElements.forEach(element => {
      element.style.opacity = '1';
      element.style.visibility = 'visible';
      element.style.display = '';
      element.style.transform = '';
    });
    
    // If animation is disabled, ensure content is immediately visible
    if (!this.animationEnabled && this.content) {
      this.content.classList.add('is-visible');
    }
  }

  applyCustomButtonStyles() {
    // Apply custom CSS properties to buttons
    this.buttons.forEach(button => {
      const isPrimary = button.classList.contains('btn--primary');
      const isSecondary = button.classList.contains('btn--secondary');
      
      // Apply gradient if available
      const gradientProp = isPrimary ? '--button-primary-gradient' : '--button-secondary-gradient';
      const gradient = getComputedStyle(this.section).getPropertyValue(gradientProp).trim();
      
      if (gradient && gradient !== '') {
        button.style.background = gradient;
        button.style.backgroundSize = '200% 200%';
        button.style.borderColor = 'transparent';
      }
      
      // Apply hover gradient if available
      const hoverGradientProp = isPrimary ? '--button-primary-gradient-hover' : '--button-secondary-gradient-hover';
      const hoverGradient = getComputedStyle(this.section).getPropertyValue(hoverGradientProp).trim();
      
      if (hoverGradient && hoverGradient !== '') {
        button.setAttribute('data-hover-gradient', hoverGradient);
      }
    });
  }

  setupButtonInteractions() {
    this.buttons.forEach(button => {
      // Add ripple effect on click
      button.addEventListener('click', (e) => {
        this.createRippleEffect(e, button);
      });
      
      // Add hover gradient animation
      button.addEventListener('mouseenter', () => {
        const hoverGradient = button.getAttribute('data-hover-gradient');
        if (hoverGradient && hoverGradient !== '') {
          button.style.background = hoverGradient;
          button.style.animation = 'gradientShift 3s ease infinite';
        }
      });
      
      button.addEventListener('mouseleave', () => {
        const gradientProp = button.classList.contains('btn--primary') 
          ? '--button-primary-gradient' 
          : '--button-secondary-gradient';
        const gradient = getComputedStyle(this.section).getPropertyValue(gradientProp).trim();
        
        if (gradient && gradient !== '') {
          button.style.background = gradient;
          button.style.animation = '';
        }
      });
      
      // Add touch feedback for mobile
      button.addEventListener('touchstart', (e) => {
        e.preventDefault();
        button.style.transform = 'scale(0.98)';
        this.isTouching = true;
        setTimeout(() => {
          this.isTouching = false;
        }, 300);
      });
      
      button.addEventListener('touchend', () => {
        button.style.transform = '';
      });
      
      // Add keyboard navigation
      button.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          button.style.transform = 'scale(0.98)';
          setTimeout(() => {
            button.style.transform = '';
          }, 150);
        }
      });
    });
  }

  createRippleEffect(event, button) {
    if (!this.customButtonEnabled) return;
    
    const ripple = document.createElement('span');
    const rect = button.getBoundingClientRect();
    
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    
    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add('ripple');
    
    button.appendChild(ripple);
    
    setTimeout(() => {
      ripple.remove();
    }, 600);
  }

  setupAnimations() {
    // Set up intersection observer for scroll-triggered animations
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.1
    };
    
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateIn();
          if (this.observer) {
            this.observer.unobserve(entry.target);
          }
        }
      });
    }, observerOptions);
    
    this.observer.observe(this.section);
    
    // Special handling for typewriter animation
    if (this.animationType === 'typewriter') {
      this.setupTypewriterAnimation();
    }
  }

  animateIn() {
    if (!this.content) return;
    
    // Add visible class to trigger CSS animations
    this.content.classList.add('is-visible');
    
    // Special handling for typewriter animation
    if (this.animationType === 'typewriter') {
      this.startTypewriterAnimation();
    }
    
    // Trigger background scale animation
    if (this.scaleEffectEnabled && !this.isMobile) {
      this.animateBackgroundScale();
    }
    
    // Log for debugging
    if (window.heroElegantDebug) {
      console.log(`Hero Elegant animated in (${this.animationType}):`, this.section.id);
    }
  }

  animateBackgroundScale() {
    if (!this.bg) return;
    
    // Reset scale
    this.bg.style.transform = 'scale(1)';
    
    // Animate to normal scale
    setTimeout(() => {
      this.bg.style.transform = 'scale(1.05)';
      this.bg.style.transition = 'transform 1.5s cubic-bezier(0.2, 0.9, 0.3, 1)';
    }, 100);
  }

  setupTypewriterAnimation() {
    const headline = this.section.querySelector('.hero-elegant__headline');
    if (!headline) return;
    
    // Store original text
    const originalText = headline.textContent;
    headline.setAttribute('data-original-text', originalText);
    
    // Clear text for animation
    headline.textContent = '';
    headline.style.width = '0';
  }

  startTypewriterAnimation() {
    const headline = this.section.querySelector('.hero-elegant__headline');
    if (!headline) return;
    
    const originalText = headline.getAttribute('data-original-text') || headline.textContent;
    const duration = parseFloat(getComputedStyle(this.section).getPropertyValue('--animation-duration')) || 800;
    const delay = parseFloat(getComputedStyle(this.section).getPropertyValue('--animation-delay')) || 400;
    
    // Reset and animate
    headline.textContent = '';
    headline.style.width = '0';
    
    // Start animation after delay
    setTimeout(() => {
      const chars = originalText.split('');
      let i = 0;
      
      const typeChar = () => {
        if (i < chars.length) {
          headline.textContent += chars[i];
          headline.style.width = `${((i + 1) / chars.length) * 100}%`;
          i++;
          setTimeout(typeChar, duration / chars.length);
        }
      };
      
      typeChar();
    }, delay);
  }

  setupScrollIndicator() {
    // Add click handler
    this.scrollIndicator.addEventListener('click', (e) => {
      e.preventDefault();
      this.scrollToNextSection();
    });
    
    // Add hover effect
    this.scrollIndicator.addEventListener('mouseenter', () => {
      this.scrollIndicator.style.opacity = '1';
      this.scrollIndicator.style.transform = 'translateX(-50%) scale(1.1)';
    });
    
    this.scrollIndicator.addEventListener('mouseleave', () => {
      this.scrollIndicator.style.opacity = '';
      this.scrollIndicator.style.transform = '';
    });
    
    // Hide on scroll
    this.scrollHandler = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const sectionBottom = this.section.offsetTop + this.section.offsetHeight;
      
      if (scrollTop > this.lastScrollTop && scrollTop > 100) {
        // Scrolling down, hide indicator
        this.hideScrollIndicator();
      } else if (scrollTop < sectionBottom - window.innerHeight) {
        // Still in hero section, show indicator
        this.showScrollIndicator();
      }
      
      this.lastScrollTop = scrollTop;
    };
    
    window.addEventListener('scroll', this.scrollHandler);
    
    // Special animations for different icon types
    this.setupScrollIconAnimations();
  }

  setupScrollIconAnimations() {
    switch (this.scrollIconType) {
      case 'mouse':
        this.setupMouseScrollAnimation();
        break;
      case 'circle-text':
        this.setupCircleTextAnimation();
        break;
    }
  }

  setupMouseScrollAnimation() {
    const mouseIcon = this.scrollIndicator.querySelector('.hero-elegant__scroll-icon--mouse');
    if (mouseIcon) {
      // Add click animation
      this.scrollIndicator.addEventListener('click', () => {
        mouseIcon.style.transform = 'scale(0.9)';
        setTimeout(() => {
          mouseIcon.style.transform = '';
        }, 200);
      });
    }
  }

  setupCircleTextAnimation() {
    const circleIcon = this.scrollIndicator.querySelector('.hero-elegant__scroll-icon--circle-text');
    if (circleIcon) {
      // Add rotation animation on hover
      this.scrollIndicator.addEventListener('mouseenter', () => {
        circleIcon.style.transform = 'rotate(180deg)';
        circleIcon.style.transition = 'transform 0.5s ease';
      });
      
      this.scrollIndicator.addEventListener('mouseleave', () => {
        circleIcon.style.transform = 'rotate(0deg)';
      });
    }
  }

  hideScrollIndicator() {
    if (this.scrollIndicator) {
      this.scrollIndicator.style.opacity = '0';
      this.scrollIndicator.style.pointerEvents = 'none';
      this.scrollIndicator.style.transform = 'translate(-50%, 10px)';
      this.scrollIndicator.style.transition = 'all 0.3s ease';
    }
  }

  showScrollIndicator() {
    if (this.scrollIndicator) {
      this.scrollIndicator.style.opacity = '';
      this.scrollIndicator.style.pointerEvents = '';
      this.scrollIndicator.style.transform = '';
      this.scrollIndicator.style.transition = 'all 0.3s ease';
    }
  }

  scrollToNextSection() {
    const nextSection = this.section.nextElementSibling;
    
    if (nextSection) {
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      const nextSectionTop = nextSection.offsetTop - headerHeight - 20;
      
      window.scrollTo({
        top: nextSectionTop,
        behavior: 'smooth'
      });
      
      // Hide indicator after click
      this.hideScrollIndicator();
    } else {
      // Scroll to bottom of page
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  setupInteractiveEffects() {
    if (this.isMobile) return; // Disable effects on mobile for performance
    
    if (this.parallaxEnabled || this.scaleEffectEnabled) {
      this.setupScrollEffects();
    }
    
    // Setup hover effects
    this.setupHoverEffects();
  }

  setupScrollEffects() {
    const updateEffects = () => {
      if (!this.bg) return;
      
      const rect = this.section.getBoundingClientRect();
      const scrollPercent = Math.max(0, Math.min(1, 1 - (rect.top + rect.height) / (window.innerHeight + rect.height)));
      
      if (this.parallaxEnabled) {
        const parallaxIntensity = parseFloat(getComputedStyle(this.section).getPropertyValue('--parallax-intensity')) || 30;
        const parallaxOffset = scrollPercent * parallaxIntensity;
        this.bg.style.transform = `translateY(${parallaxOffset}px)`;
      }
      
      if (this.scaleEffectEnabled) {
        const scaleIntensity = parseFloat(getComputedStyle(this.section).getPropertyValue('--scale-intensity')) || 0.05;
        const scale = 1 + (scrollPercent * scaleIntensity);
        this.bg.style.transform += ` scale(${scale})`;
      }
    };
    
    // Throttle scroll events for performance
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateEffects();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', scrollHandler);
    window.addEventListener('resize', scrollHandler);
    
    // Initial call
    updateEffects();
    
    // Store handlers for cleanup
    this.scrollEffectsHandler = scrollHandler;
  }

  setupHoverEffects() {
    // Headline hover effect
    const headline = this.section.querySelector('.hero-elegant__headline');
    if (headline) {
      headline.addEventListener('mouseenter', () => {
        if (!this.isMobile) {
          headline.style.transform = 'translateY(-2px)';
          headline.style.transition = 'transform 0.3s ease';
        }
      });
      
      headline.addEventListener('mouseleave', () => {
        headline.style.transform = '';
      });
    }
    
    // Eyebrow hover effect
    const eyebrow = this.section.querySelector('.hero-elegant__eyebrow-text');
    if (eyebrow) {
      eyebrow.addEventListener('mouseenter', () => {
        if (!this.isMobile) {
          const currentLS = parseFloat(getComputedStyle(eyebrow).letterSpacing);
          eyebrow.style.letterSpacing = `${currentLS + 0.05}em`;
          eyebrow.style.transition = 'letter-spacing 0.3s ease';
        }
      });
      
      eyebrow.addEventListener('mouseleave', () => {
        eyebrow.style.letterSpacing = '';
      });
    }
    
    // Overlay hover effect
    if (this.overlay) {
      this.section.addEventListener('mouseenter', () => {
        if (!this.isMobile) {
          const currentOpacity = parseFloat(getComputedStyle(this.overlay).opacity);
          this.overlay.style.opacity = `${Math.min(currentOpacity + 0.05, 0.8)}`;
          this.overlay.style.transition = 'opacity 0.3s ease';
        }
      });
      
      this.section.addEventListener('mouseleave', () => {
        this.overlay.style.opacity = '';
      });
    }
  }

  setupTouchInteractions() {
    if (!this.isMobile) return;
    
    // Touch feedback for section
    this.section.addEventListener('touchstart', (e) => {
      this.touchStartY = e.touches[0].clientY;
      this.section.style.transform = 'scale(0.995)';
      this.section.style.transition = 'transform 0.2s ease';
    });
    
    this.section.addEventListener('touchend', () => {
      this.section.style.transform = '';
      setTimeout(() => {
        this.section.style.transform = '';
      }, 200);
    });
    
    // Prevent vertical scroll when touching buttons
    this.buttons.forEach(button => {
      button.addEventListener('touchmove', (e) => {
        if (this.isTouching) {
          e.preventDefault();
        }
      }, { passive: false });
    });
  }

  setupResponsive() {
    const handleResize = () => {
      this.isMobile = window.innerWidth < 768;
      
      // Adjust animation settings for mobile
      if (this.isMobile) {
        this.section.style.setProperty('--animation-delay', '200ms');
        this.section.style.setProperty('--animation-duration', '600ms');
        
        // Disable typewriter animation on mobile
        if (this.animationType === 'typewriter') {
          const headline = this.section.querySelector('.hero-elegant__headline');
          if (headline && headline.getAttribute('data-original-text')) {
            headline.textContent = headline.getAttribute('data-original-text');
            headline.style.width = '100%';
          }
        }
        
        // Disable parallax and scale on mobile
        if (this.bg) {
          this.bg.style.transform = '';
          this.bg.style.transition = '';
        }
      } else {
        this.section.style.setProperty('--animation-delay', '400ms');
        this.section.style.setProperty('--animation-duration', '800ms');
      }
      
      // Adjust button sizes
      this.adjustButtonSizes();
      
      // Update background based on mobile/desktop
      this.updateBackgroundVisibility();
    };
    
    // Initial call
    handleResize();
    
    // Listen for resize
    this.resizeHandler = handleResize;
    window.addEventListener('resize', handleResize);
  }

  updateBackgroundVisibility() {
    const hasMobileBg = this.section.classList.contains('has-mobile-bg');
    
    if (hasMobileBg && this.isMobile) {
      // Show mobile background
      const mobileBg = this.section.querySelector('.hero-elegant__bg-mobile');
      const desktopBg = this.section.querySelector('.hero-elegant__bg-desktop');
      
      if (mobileBg) mobileBg.style.display = 'block';
      if (desktopBg) desktopBg.style.display = 'none';
    }
  }

  adjustButtonSizes() {
    const buttons = this.section.querySelectorAll('.btn');
    
    buttons.forEach(button => {
      if (this.isMobile) {
        // Add mobile-specific class
        button.classList.add('btn--mobile');
      } else {
        button.classList.remove('btn--mobile');
      }
    });
  }

  addDebugInfo() {
    console.log('Hero Elegant Debug:', {
      id: this.id,
      animationEnabled: this.animationEnabled,
      animationType: this.animationType,
      scrollIconType: this.scrollIconType,
      customButtonEnabled: this.customButtonEnabled,
      parallaxEnabled: this.parallaxEnabled,
      scaleEffectEnabled: this.scaleEffectEnabled,
      isMobile: this.isMobile,
      element: this.section
    });
  }

  destroy() {
    // Cleanup observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Cleanup event listeners
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    
    if (this.scrollEffectsHandler) {
      window.removeEventListener('scroll', this.scrollEffectsHandler);
      window.removeEventListener('resize', this.scrollEffectsHandler);
    }
    
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    
    // Cleanup scroll indicator
    if (this.scrollIndicator) {
      this.scrollIndicator.removeEventListener('click', this.scrollToNextSection);
    }
    
    // Cleanup buttons
    this.buttons.forEach(button => {
      button.removeEventListener('click', this.createRippleEffect);
      button.removeEventListener('mouseenter', this.handleMouseEnter);
      button.removeEventListener('mouseleave', this.handleMouseLeave);
      button.removeEventListener('touchstart', this.handleTouchStart);
      button.removeEventListener('touchend', this.handleTouchEnd);
    });
  }
}

// Initialize all hero elegant sections
document.addEventListener('DOMContentLoaded', () => {
  const heroSections = document.querySelectorAll('[data-hero-elegant]');
  
  heroSections.forEach((section, index) => {
    // Initialize
    const hero = new HeroElegant(section);
    section.heroElegantInstance = hero;
  });
  
  // Debug mode
  if (window.location.search.includes('debug=hero')) {
    window.heroElegantDebug = true;
    console.log(`Hero Elegant: ${heroSections.length} sections initialized`);
  }
});

// Shopify theme editor compatibility
if (typeof Shopify !== 'undefined') {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('[data-hero-elegant]');
    if (section) {
      const hero = new HeroElegant(section);
      section.heroElegantInstance = hero;
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-hero-elegant]');
    if (section && section.heroElegantInstance) {
      section.heroElegantInstance.destroy();
    }
  });
  
  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector('[data-hero-elegant]');
    if (section && section.heroElegantInstance) {
      // Re-animate when selected in editor
      section.heroElegantInstance.animateIn();
    }
  });
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HeroElegant;
}

// Global access for debugging
window.HeroElegant = HeroElegant;